import React, { useEffect, useState } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5percent from "@amcharts/amcharts5/percent";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import * as am5xy from "@amcharts/amcharts5/xy";
import DescriptionHeadings from "@/shared/components/atoms/DescriptionHeadings";
import { t } from "i18next";
import {
  useChartBranchesQuery,
  useChartsUsersAgeQuery,
  useChartsUsersPerMonthQuery,
} from "@/app/kpi/kpi.api";
import {
  configureExporting,
  generateYearsOptions,
  initializeAmChart,
  Language,
  months,
} from "@/shared/utils/chartUtils";
import { Select } from "@/shared/components/organisms/Select";
import { BranchData } from "@/app/kpi/kpi.types";
import { Branch } from "@/app/branchs/branchs.type";
import {
  Option,
  OptionsSelect,
} from "../../../shared/components/molecules/OptionsSelect";
import { Percent } from "@amcharts/amcharts5";
import { faLoader } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { set } from "date-fns";

export type ClientProfileProps = {
  branches: Branch[];
};

const ClientProfileComponent: React.FC<ClientProfileProps> = ({ branches }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isLoading2, setIsLoading2] = useState(true);
  const [isLoading3, setIsLoading3] = useState(true);
  const yearsOptions = generateYearsOptions(
    new Date().getFullYear() - 10,
    new Date().getFullYear()
  );

  const [branchSelected, setBranchSelected] = useState<Option>(() => {
    const saved = localStorage.getItem("branchSelected");
    return saved ? JSON.parse(saved) : undefined;
  });
  const [branchSelected2, setBranchSelected2] = useState<Option>(() => {
    const saved = localStorage.getItem("branchSelected2");
    return saved ? JSON.parse(saved) : undefined;
  });
  const [yearSelected, setYearSelected] = useState<Option>(() => {
    const saved = localStorage.getItem("yearSelected");
    return saved ? JSON.parse(saved) : undefined;
  });

  const { data: branchesData, error: branchesError } = useChartBranchesQuery();
  const {
    data: usersPerMonthData,
    error: usersPerMonthError,
    refetch: refetchUsersPerMonth,
  } = useChartsUsersPerMonthQuery({
    filial: branchSelected?.id,
    year: yearSelected ? yearSelected.id : null,
  });

  const { data: usersByGenderData, error: usersByGenderError } =
    useChartsUsersAgeQuery({ filial: branchSelected2?.id });
  const [branchesOptions, setBranchesOptions] = useState<
    { id: number; name: string }[]
  >([]);

  useEffect(() => {
    if (branches) {
      setBranchesOptions(
        branches.map((branch: Branch) => ({
          id: branch.id,
          name: branch.branch,
        }))
      );
    }
  }, [branches]);

  useEffect(() => {
    const currentYear = new Date().getFullYear();

    if (branchesOptions.length > 0 && !branchSelected) {
      setBranchSelected(branchesOptions[0]);
    }
    if (branchesOptions.length > 0 && !branchSelected2) {
      setBranchSelected2(branchesOptions[0]);
    }
    if (yearsOptions.length > 0 && !yearSelected) {
      const defaultYear = yearsOptions.find((year) => year.id === currentYear);
      setYearSelected(defaultYear || yearsOptions[0]);
    }
  }, [branchesOptions, yearsOptions, branchSelected, yearSelected]);

  useEffect(() => {
    if (branchesData) {
      const root = createBranchesChart(
        branchesData.transformedData,
        branchesData.total
      );
      return () => {
        root.dispose();
      };
    }
  }, [branchesData]);

  useEffect(() => {
    if (branchSelected || yearSelected) {
      refetchUsersPerMonth();
    }
  }, [branchSelected, yearSelected, refetchUsersPerMonth]);

  useEffect(() => {
    if (usersByGenderData) {
      if (document.getElementById("chartdiv")) {
        const data = Object.keys(usersByGenderData)
          .map((key) => {
            const item = usersByGenderData[key];
            return item["male"] !== 0 || item["female"] !== 0
              ? { age: key, male: -item["male"], female: item["female"] }
              : null;
          })
          .filter(Boolean);
        createUsersByGender(data);
      }
    }
  }, [usersByGenderData]);

  useEffect(() => {
    const systemLanguage: Language = navigator.language.slice(0, 2) as Language;
    const selectedMonths = months[systemLanguage] || months["en"];

    if (usersPerMonthData) {
      const data = usersPerMonthData.map((item: number[], key: number) => ({
        category: selectedMonths[key],
        value: item,
      }));

      const maxValues = Math.max(
        ...usersPerMonthData.map((item: { value: number }) => item.value)
      );
      createTotalUserPerMonth(data, maxValues);
    }
  }, [usersPerMonthData]);

  const createBranchesChart = (
    data: BranchData[],
    totalSum: number
  ): am5.Root => {
    const root = am5.Root.new("branches");

    root.setThemes([am5themes_Animated.new(root)]);
    if (root._logo) {
      root._logo.dispose();
    }

    const chart = root.container.children.push(
      am5percent.PieChart.new(root, {
        layout: root.verticalLayout,
        innerRadius: am5.percent(50),
      })
    );

    const series = chart.series.push(
      am5percent.PieSeries.new(root, {
        valueField: "value",
        categoryField: "category",
        alignLabels: false,
      })
    );

    series.children.push(
      am5.Label.new(root, {
        centerX: am5.percent(50),
        centerY: am5.percent(50),
        text: "Total: " + totalSum,
        populateText: true,
        fontSize: "1.2em",
      })
    );

    series.labels.template.setAll({
      forceHidden: true,
    });

    series.slices.template.set(
      "tooltipText",
      "{category} - Total: [bold]{value}[/]"
    );

    series.data.setAll(data);

    // Crear un contenedor para la leyenda con tamaño fijo y scroll
    const legendContainer = chart.children.push(
      am5.Container.new(root, {
        width: am5.percent(100),
        height: am5.percent(30), // Ajusta la altura según sea necesario
        layout: root.verticalLayout,
      })
    );

    const legend = legendContainer.children.push(
      am5.Legend.new(root, {
        marginTop: 15,
        marginBottom: 15,

        centerX: am5.p50,
        x: am5.p50,
        height: am5.percent(30),
        width: am5.percent(80),

        verticalScrollbar: am5.Scrollbar.new(root, {
          orientation: "vertical",
        }),
      })
    );

    // Configurar las etiquetas de la leyenda para que sean de color gris y tamaño de fuente 11
    legend.labels.template.setAll({
      fontSize: 12,
      fontWeight: "300",
      fill: am5.color(0x6b7280), // text-gray-500
    });

    // Configurar los valores de la leyenda para que sean de color gris y tamaño de fuente 11
    legend.valueLabels.template.setAll({
      fontSize: 12,
      fontWeight: "300",
      fill: am5.color(0x6b7280), // text-gray-500
    });

    legend.data.setAll(series.dataItems);

    series.appear(1000, 100);

    setIsLoading(false);

    configureExporting(root, data);

    return root;
  };

  const createTotalUserPerMonth = (data: any, maxValue: number) => {
    const root = initializeAmChart("users_month");

    root.setThemes([am5themes_Animated.new(root)]);
    if (root._logo) {
      root._logo.dispose();
    }

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: undefined,
        wheelY: undefined,
        pinchZoomX: true,
        paddingLeft: 0,
        paddingRight: 1,
      })
    );

    const cursor = chart.set("cursor", am5xy.XYCursor.new(root, {}));
    cursor.lineY.set("visible", false);

    const xRenderer = am5xy.AxisRendererX.new(root, {
      minGridDistance: 30,
      minorGridEnabled: true,
    });

    xRenderer.labels.template.setAll({
      rotation: 0, // Cambiar rotación a 0 para texto horizontal
      centerY: am5.p50,
      centerX: am5.p50, // Ajustar centrado horizontal
      paddingRight: 15,
    });

    xRenderer.grid.template.setAll({
      location: 1,
      visible: false,
    });

    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        maxDeviation: 0.3,
        categoryField: "category",
        renderer: xRenderer,
        tooltip: am5.Tooltip.new(root, {}),
      })
    );

    const yRenderer = am5xy.AxisRendererY.new(root, {
      strokeOpacity: 0.1,
    });

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: yRenderer,
        min: 0,
        max: maxValue < 5 ? 5 : maxValue,
        strictMinMax: true,
        numberFormat: "#",
      })
    );

    yAxis.get("renderer").labels.template.setAll({
      fill: am5.color(0x6b7280), // text-gray-500
    });

    xAxis.get("renderer").labels.template.setAll({
      fill: am5.color(0x6b7280), // text-gray-500
    });

    const series = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: "Series 1",
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: "value",
        sequencedInterpolation: true,
        categoryXField: "category",
        tooltip: am5.Tooltip.new(root, {
          labelText: `${t("share.views.branchs")}: {valueY} ${t(
            "share.views.chart_user"
          )}`,
        }),
      })
    );

    series.strokes.template.setAll({
      strokeWidth: 2,
      stroke: am5.color("#68478D"),
    });

    xAxis.data.setAll(data);
    series.data.setAll(data);

    configureExporting(root, data);

    setIsLoading2(false);

    series.appear(1000);
    chart.appear(1000, 100);
  };

  const createUsersByGender = (data: any) => {
    const root = initializeAmChart("chartdiv");

    root.setThemes([am5themes_Animated.new(root)]);
    if (root._logo) {
      root._logo.dispose();
    }

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: undefined,
        wheelY: undefined,
        layout: root.verticalLayout,
        paddingLeft: 0,
        paddingRight: 10,
      })
    );

    chart.zoomOutButton.set("forceHidden", true);
    chart.getNumberFormatter().set("numberFormat", "#.#s");

    const yAxis = chart.yAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "age",
        renderer: am5xy.AxisRendererY.new(root, {
          inversed: true,
          cellStartLocation: 0.1,
          cellEndLocation: 0.9,
          minorGridEnabled: true,
          minGridDistance: 20,
        }),
      })
    );

    yAxis.get("renderer").labels.template.setAll({
      fill: am5.color(0x6b7280),
    });

    yAxis.data.setAll(data);

    const xAxis = chart.xAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: 60,
          strokeOpacity: 0.1,
        }),
      })
    );

    // Cambiar el color de las etiquetas del eje X
    xAxis.get("renderer").labels.template.setAll({
      fill: am5.color(0x6b7280), // text-gray-500
    });

    const createSeries = (
      field: string,
      labelCenterX: number | Percent | undefined,
      pointerOrientation:
        | "left"
        | "right"
        | "up"
        | "down"
        | "vertical"
        | "horizontal",
      rangeValue: number,
      title: string
    ) => {
      const series = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          xAxis: xAxis,
          yAxis: yAxis,
          valueXField: field,
          categoryYField: "age",
          sequencedInterpolation: true,
          clustered: false,
          tooltip: am5.Tooltip.new(root, {
            pointerOrientation: pointerOrientation,
            labelText: `${title}: {valueX}`,
          }),
        })
      );

      series.columns.template.setAll({
        height: am5.p100,
        strokeOpacity: 0,
        fillOpacity: 0.8,
      });

      series.bullets.push(() =>
        am5.Bullet.new(root, {
          locationX: 1,
          locationY: 0.5,
          sprite: am5.Label.new(root, {
            centerY: am5.p50,
            text: "{valueX}",
            populateText: true,
            centerX: labelCenterX,
          }),
        })
      );

      series.data.setAll(data);
      series.appear();

      return series;
    };

    const male = t("user.views.male");
    const female = t("user.views.female");

    createSeries("male", am5.p100, "right", -4, male);
    createSeries("female", 0, "left", 4, female);

    const cursor = chart.set(
      "cursor",
      am5xy.XYCursor.new(root, {
        behavior: "zoomY",
      })
    );

    cursor.lineY.set("forceHidden", true);
    cursor.lineX.set("forceHidden", true);

    chart.appear(1000, 100);

    setIsLoading3(false);

    configureExporting(root, data);
  };

  if (branchesError || usersPerMonthError || usersByGenderError) {
    return <div>Error loading data</div>;
  }

  return (
    <>
      <div className="p-4">
        <DescriptionHeadings
          title={t("share.views.totalusers")}
          description={t("share.views.clients_profile")}
        />
      </div>
      <div className="grid grid-cols-12 gap-4 p-4">
        <div className="bg-white shadow rounded-lg p-4 col-span-12 md:col-span-4 sm:col-span-12">
          <h2 className="text-center  font-bold leading-6 text-gray-900">
            {t("share.views.kpi_user_branch")}
          </h2>
          {isLoading && (
            <div className="flex justify-center items-center h-90 mt-20">
              <FontAwesomeIcon icon={faLoader} spin className="text-6xl" />
            </div>
          )}
          <div id="branches" style={{ width: "100%", height: "400px" }}></div>
        </div>
        <div className="bg-white shadow rounded-lg p-4 col-span-12 md:col-span-8 sm:col-span-12">
          <h2 className="text-center  font-bold leading-6 text-gray-900">
            {t("share.views.total_user")}
          </h2>
          <div className="flex justify-end gap-4 ">
            <Select
              name="branch"
              selected={branchSelected}
              setSelected={setBranchSelected}
              options={branchesOptions}
              tittle={t("user.views.branch")}
            />
            <Select
              name="year"
              selected={yearSelected}
              setSelected={setYearSelected}
              options={yearsOptions}
              tittle={t("user.views.year")}
            />
          </div>
          {isLoading2 && (
            <div className="flex justify-center items-center h-90 mt-20">
              <FontAwesomeIcon icon={faLoader} spin className="text-6xl" />
            </div>
          )}
          <div
            id="users_month"
            style={{ width: "100%", height: "400px" }}
          ></div>
        </div>
        <div className="bg-white shadow rounded-lg p-4 col-span-12">
          <h2 className="text-center  font-bold leading-6 text-gray-900">
            {t("share.views.age_user")}
          </h2>
          <div>
            <div className="flex justify-end gap-4 ">
              <Select
                name="branch"
                selected={branchSelected2}
                setSelected={setBranchSelected2}
                options={branchesOptions}
                tittle={t("user.views.branch")}
              />
              <Select
                name="year"
                selected={yearSelected}
                setSelected={setYearSelected}
                options={yearsOptions}
                tittle={t("user.views.year")}
              />
            </div>
          </div>
          {isLoading3 && (
            <div className="flex justify-center items-center h-90 mt-20">
              <FontAwesomeIcon icon={faLoader} spin className="text-6xl" />
            </div>
          )}
          <div id="chartdiv" style={{ width: "100%", height: "400px" }}></div>
        </div>
      </div>
    </>
  );
};

export default ClientProfileComponent;
